import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, DollarSign, Calendar, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentHistory = ({ payments }) => {
  const { t } = useTranslation();
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { className: 'bg-green-600', labelKey: 'payments.completed' },
      pending: { className: 'bg-yellow-600', labelKey: 'payments.pending' },
      failed: { className: 'bg-red-600', labelKey: 'payments.failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge className={config.className}>{t(config.labelKey)}</Badge>;
  };

  const downloadReceipt = async (paymentId) => {
    try {
      setDownloadingReceipt(paymentId);
      toast.info(t('payments.generatingReceipt'));
      
      // First, fetch the receipt data as JSON
      const response = await axios.get(`${API}/payments/${paymentId}/receipt`);
      const receiptData = response.data;
      
      // Generate PDF using jsPDF
      const { jsPDF } = await import('jspdf');
      const pdf = new jsPDF();
      
      // Add MyMedMember branding
      pdf.setFillColor(20, 184, 166); // Teal color
      pdf.rect(0, 0, 210, 40, 'F');
      
      // Company name
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('MyMedMember', 105, 17, { align: 'center' });
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text('by Clinicax Systems', 105, 25, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text('Healthcare Membership Platform', 105, 34, { align: 'center' });
      
      // Receipt title
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('PAYMENT RECEIPT', 105, 55, { align: 'center' });
      
      // Receipt details
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      let y = 75;
      const leftMargin = 20;
      const lineHeight = 8;
      
      // Receipt info
      pdf.setFont('helvetica', 'bold');
      pdf.text('Receipt Number:', leftMargin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(receiptData.receipt_number, leftMargin + 50, y);
      y += lineHeight;
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Date:', leftMargin, y);
      pdf.setFont('helvetica', 'normal');
      pdf.text(new Date(receiptData.payment_date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }), leftMargin + 50, y);
      y += lineHeight * 2;
      
      // Patient info
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Patient Information', leftMargin, y);
      pdf.setFontSize(11);
      y += lineHeight;
      
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Name: ${receiptData.patient_name}`, leftMargin, y);
      y += lineHeight;
      pdf.text(`Patient ID: ${receiptData.patient_id}`, leftMargin, y);
      y += lineHeight * 2;
      
      // Payment details
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Payment Details', leftMargin, y);
      pdf.setFontSize(11);
      y += lineHeight;
      
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Description: ${receiptData.description || 'Payment'}`, leftMargin, y);
      y += lineHeight;
      pdf.text(`Payment Method: ${receiptData.payment_method.toUpperCase()}`, leftMargin, y);
      y += lineHeight;
      pdf.text(`Processed By: ${receiptData.processed_by_name}`, leftMargin, y);
      y += lineHeight * 2;
      
      // Amount section with box
      pdf.setFillColor(240, 240, 240);
      pdf.rect(leftMargin, y, 170, 20, 'F');
      
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Amount Paid:', leftMargin + 5, y + 13);
      
      pdf.setTextColor(20, 184, 166);
      pdf.text(formatCurrency(receiptData.amount), 170, y + 13, { align: 'right' });
      pdf.setTextColor(0, 0, 0);
      
      y += 35;
      
      // Footer
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(128, 128, 128);
      pdf.text('Thank you for your payment!', 105, y, { align: 'center' });
      y += 5;
      pdf.text('For questions, contact support@mymedmember.com', 105, y, { align: 'center' });
      
      // Add footer line
      y = 280;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(20, y, 190, y);
      pdf.setFontSize(8);
      pdf.text(`© ${new Date().getFullYear()} Clinicax Systems. All rights reserved. MyMedMember™ is a trademark of Clinicax Systems.`, 105, y + 5, { align: 'center' });
      
      // Save PDF
      pdf.save(`receipt-${receiptData.receipt_number}.pdf`);
      toast.success(t('payments.receiptDownloaded'));
      
    } catch (error) {
      console.error('Failed to download receipt:', error);
      toast.error(t('payments.failedToDownload'));
    } finally {
      setDownloadingReceipt(null);
    }
  };

  if (!payments || payments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('payments.paymentHistory')}</CardTitle>
          <CardDescription>{t('payments.viewPaymentsAndReceipts')}</CardDescription>
        </CardHeader>
        <CardContent className="text-center py-12">
          <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('payments.noPaymentHistory')}</p>
        </CardContent>
      </Card>
    );
  }

  // Calculate totals
  const totalPaid = payments
    .filter(p => p.payment_status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('payments.totalPaid')}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalPaid)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('payments.totalTransactions')}</p>
                <p className="text-2xl font-bold text-gray-900">{payments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">{t('payments.lastPayment')}</p>
                <p className="text-lg font-bold text-gray-900">
                  {new Date(payments[0]?.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t('payments.paymentHistory')}</CardTitle>
          <CardDescription>{t('payments.allTransactions')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>{t('payments.date')}</TableHead>
                  <TableHead>{t('payments.description')}</TableHead>
                  <TableHead>{t('payments.method')}</TableHead>
                  <TableHead>{t('payments.amount')}</TableHead>
                  <TableHead>{t('payments.status')}</TableHead>
                  <TableHead>{t('payments.receipt')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} className="hover:bg-gray-50">
                    <TableCell>
                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="font-medium">
                      <div>
                        {payment.description || 'Payment'}
                        {payment.enrollment_fee > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {t('payments.includesEnrollmentFee')}: {formatCurrency(payment.enrollment_fee)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="capitalize">{payment.payment_method || 'N/A'}</span>
                    </TableCell>
                    <TableCell className="font-semibold">
                      <div>
                        {formatCurrency(payment.amount)}
                        {payment.enrollment_fee > 0 && payment.base_amount && (
                          <div className="text-xs text-gray-500 mt-1">
                            {t('payments.plan')}: {formatCurrency(payment.base_amount)}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(payment.payment_status)}</TableCell>
                    <TableCell>
                      {payment.payment_status === 'completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadReceipt(payment.id)}
                          disabled={downloadingReceipt === payment.id}
                        >
                          {downloadingReceipt === payment.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentHistory;
